import '../../../../pages/Admin/Admin.scss';
import '../../../../styles/_table.scss';

import { Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import { useCallback, useMemo, useState } from 'react';

import { ReactComponent as ArrowDownIcon } from 'images/icons/ArrowDown.svg';
import { ReactComponent as ArrowUpIcon } from 'images/icons/ArrowUp.svg';
import { DefaultRoutes } from 'Routes';
import { ReactComponent as InfoIcon } from 'images/icons/Info.svg';
import LessonPreviewModal from '../../Modals/LessonPreviewModal/LessonPreviewModal';
import { Link } from 'react-router-dom';
import { ReactComponent as MagnifierIcon } from 'images/icons/Magnifier.svg';
import { ReactComponent as MinusIcon } from 'images/icons/MinusInCircle.svg';
import Paragraph from 'components/TextComps/Paragraph';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import Tooltip from '@trackman/web-shared-components/components/Tooltip/Tooltip';
import { getLanguageText } from 'utilities/languages';
import { moveLessonId } from 'utilities/sortLessons';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';

type RowsDisplay = {
  id: string;
  title: string;
  languages: string;
  description: string;
};

export const AddedLessonsTable = observer(() => {
  const ls = useStores().lessonEditorStore;
  const ces = useStores().courseEditorStore;
  const [isModalOn, setIsModalOn] = useState(false);

  const rowsDisplay: RowsDisplay[] = useMemo(
    () =>
      ces.course.lessonIds
        .map((id) => {
          const lesson = ls.lessonsList.find((lesson) => lesson.id === id);
          if (lesson === undefined) return undefined;
          return {
            id: id,
            title: lesson.titles.find((title) => title.countryCode === 'en-US')?.text,
            languages: lesson.languages ? lesson.languages.map((lng) => getLanguageText(lng)).join(', ') : '',
            description: lesson.descriptions.find((description) => description.countryCode === 'en-US')?.text,
          };
        })
        .filter((row) => row !== undefined) as RowsDisplay[],
    [JSON.stringify(ls.lessonsList), JSON.stringify(ces.course.lessonIds), ces.course.lessonIds, ls.lessonsList]
  );

  const lessonRawPreview = useCallback(
    () =>
      JSON.stringify(
        ls.lessonsList.find((lesson) => lesson.id === ls.selectedLessonId),
        null,
        2
      ),
    [ls.lessonsList, ls.selectedLessonId]
  );

  return (
    <div className='tmu-table-wrapper admin-edit-section'>
      <div className='table-subheading'>
        <strong>Added lessons</strong>
      </div>
      <Table sx={{ minWidth: 750 }} className='tmu-table admin-table'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 300 }}>Name</TableCell>
            <TableCell sx={{ width: 900 }}>Languages</TableCell>
            <TableCell sx={{ width: 200 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rowsDisplay.length ? (
            rowsDisplay.map((lesson, index) => {
              return (
                <TableRow hover key={lesson.id}>
                  <TableCell>
                    <div className='description-tooltip'>
                      <button
                        className='row-button'
                        onClick={() =>
                          ces.course.lessonIds?.length
                            ? (ces.course.lessonIds = ces.course.lessonIds?.filter((id) => id !== lesson.id))
                            : {}
                        }
                      >
                        <MinusIcon />
                      </button>
                      {lesson.title.toLowerCase()}
                      <Tooltip
                        title={lesson.description !== '' ? lesson.description : 'No description'}
                        placement='bottom'
                        className='table-cell-tooltip'
                      >
                        <InfoIcon />
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell>{lesson.languages}</TableCell>
                  <TableCell>
                    <div className='action-buttons'>
                      <button
                        className='row-button'
                        onClick={() => {
                          ls.selectedLessonId = lesson.id;
                          setIsModalOn(true);
                        }}
                      >
                        <MagnifierIcon />
                      </button>
                      <Link to={`/Admin/${DefaultRoutes.LessonEditor}/${lesson.id}`} className='row-button'>
                        <PencilIcon />
                      </Link>
                      <button
                        className='row-button'
                        style={index === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                        onClick={() => moveLessonId(ces.course.lessonIds, ls.lessonsList, lesson.id, -1)}
                      >
                        <ArrowUpIcon />
                      </button>
                      <button
                        className='row-button'
                        style={index === rowsDisplay.length - 1 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                        onClick={() => moveLessonId(ces.course.lessonIds, ls.lessonsList, lesson.id, 1)}
                      >
                        <ArrowDownIcon />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={3}>
                <Paragraph>
                  <>No lessons have been added.</>
                </Paragraph>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {isModalOn ? <LessonPreviewModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} jsonPreview={lessonRawPreview()} /> : <></>}
    </div>
  );
});

export default AddedLessonsTable;
